export type EmailVariableSource =
  | 'recipient_first_name'
  | 'recipient_full_name'
  | 'recipient_email'
  | 'recipient_phone'
  | 'literal'
  | 'empty';

export type EmailVariableMapping = {
  source: EmailVariableSource;
  literal?: string;
};

export type EmailVariableMap = Record<string, EmailVariableMapping>;

export type EmailVariableRecipient = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
};

export const EMAIL_VARIABLE_SOURCE_OPTIONS: Array<{
  value: EmailVariableSource;
  label: string;
}> = [
  { value: 'recipient_first_name', label: 'Recipient first name' },
  { value: 'recipient_full_name', label: 'Recipient full name' },
  { value: 'recipient_email', label: 'Recipient email' },
  { value: 'recipient_phone', label: 'Recipient phone' },
  { value: 'literal', label: 'Fixed text' },
  { value: 'empty', label: 'Remove (empty)' },
];

export function extractEmailTemplateVariables(
  ...parts: Array<string | null | undefined>
) {
  const keys = new Set<string>();

  for (const part of parts) {
    if (!part?.trim()) {
      continue;
    }

    const pattern = /\{\{\s*([^}]+?)\s*\}\}/g;
    let match = pattern.exec(part);

    while (match) {
      const key = match[1]?.trim();

      if (key) {
        keys.add(key);
      }

      match = pattern.exec(part);
    }
  }

  return Array.from(keys).sort((left, right) => left.localeCompare(right));
}

export function guessEmailVariableSource(key: string): EmailVariableSource {
  const normalized = key.toLowerCase();

  if (normalized.includes('email')) {
    return 'recipient_email';
  }

  if (normalized.includes('phone')) {
    return 'recipient_phone';
  }

  if (
    normalized.includes('full') ||
    normalized.includes('last') ||
    normalized.endsWith('.name') ||
    normalized === 'name'
  ) {
    return 'recipient_first_name';
  }

  if (normalized.includes('first')) {
    return 'recipient_first_name';
  }

  return 'recipient_first_name';
}

export function buildDefaultEmailVariableMap(
  keys: string[],
  existing?: EmailVariableMap | null,
): EmailVariableMap {
  const map: EmailVariableMap = { ...(existing || {}) };

  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = { source: guessEmailVariableSource(key) };
    }
  });

  Object.keys(map).forEach((key) => {
    if (!keys.includes(key)) {
      delete map[key];
    }
  });

  return map;
}

export function resolveEmailVariableValue(
  mapping: EmailVariableMapping,
  recipient: EmailVariableRecipient,
) {
  const firstName = recipient.firstName?.trim() || 'there';
  const fullName =
    [recipient.firstName, recipient.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() || firstName;

  switch (mapping.source) {
    case 'recipient_first_name':
      return firstName;
    case 'recipient_full_name':
      return fullName;
    case 'recipient_email':
      return recipient.email?.trim() || '';
    case 'recipient_phone':
      return recipient.phone?.trim() || '';
    case 'literal':
      return mapping.literal?.trim() || '';
    case 'empty':
      return '';
    default:
      return '';
  }
}

export function interpolateEmailWithVariableMap(
  content: string,
  variableMap: EmailVariableMap | null | undefined,
  recipient: EmailVariableRecipient,
) {
  return content.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, rawKey: string) => {
    const key = rawKey.trim();
    const mapping = variableMap?.[key];

    if (mapping) {
      return resolveEmailVariableValue(mapping, recipient);
    }

    return resolveEmailVariableValue(
      { source: guessEmailVariableSource(key) },
      recipient,
    );
  });
}
